import { Injectable } from '@angular/core';
import { ApiService } from '@core/api/api.service';
import {
  Subscription,
  SubscriptionTier,
  SubscriptionAddon,
  TrialStatus,
  UsageStatistics,
  FeatureAccess,
  GpOwnFundDdqQuota,
  PlanChangePreview,
  PendingPlanChange,
} from '@core/models/subscription.model';
import { GpOwnFundDdqCanStartResponse } from './gp-ddq.constants';
import { Observable, of, throwError, timer, Subscription as RxjsSubscription } from 'rxjs';
import { catchError, exhaustMap, finalize, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { HttpParams } from '@angular/common/http';
import { SubscriptionStore } from './subscription.store';
import {
  extractProfileSubscriptionPayload,
  mergeProfileIntoSubscription,
} from './profile-subscription.mapper';
import { AppStoreQuery } from '@core/state/app-state.query';
import { AppStoreService } from '@core/state/app-state.service';
import { OrganizationQuery } from '@core/organization/organization.query';
import { UserProfileDTO } from '@core/dto/user-profile.dto';
import { UserProfile } from '@core/models/user-profile.model';
import { deserialize } from '@junte/serialize-ts';
import { isNoSubscriptionFoundError } from './subscription-error.util';

@Injectable({ providedIn: 'root' })
export class SubscriptionService {
  private readonly baseUrl = 'common/subscription';
  private trialStatusInFlight: Observable<TrialStatus | null> | null = null;
  /** Dedupes parallel current/ calls while a request is in flight. */
  private currentSubscriptionInFlight: Observable<Subscription | null> | null = null;
  /** Org the in-flight request above was made for; must match before reusing it. */
  private inFlightOrganizationId: string | number | null = null;
  /** True after current/ succeeded or returned an expected "no subscription" response. */
  private hasResolvedCurrentSubscription = false;
  private resolvedOrganizationId: string | number | null = null;
  /** KAP-1849 (AC #8): tracks the single in-flight activation poll, if any. */
  private activationPollSub: RxjsSubscription | null = null;

  constructor(
    private apiService: ApiService,
    private store: SubscriptionStore,
    private appStoreQuery: AppStoreQuery,
    private appStoreService: AppStoreService,
    private organizationQuery: OrganizationQuery,
  ) {}

  private isStaffUser(): boolean {
    const user = this.appStoreQuery.getValue()?.user;
    return user?.isStaff === true;
  }

  private isSuperUser(): boolean {
    const user = this.appStoreQuery.getValue()?.user;
    return user?.isSuperUser === true;
  }

  private bypassGpOwnFundDdqCanStartForPrivilegedUser(): GpOwnFundDdqCanStartResponse {
    const now = new Date();
    return {
      allowed: true,
      denial_reason: null,
      period: { month: now.getMonth() + 1, year: now.getFullYear() },
      limit: 0,
      used: 0,
      remaining: 1,
    };
  }

  private isAuthenticated(): boolean {
    return Boolean(this.appStoreQuery.getValue()?.authentication?.token);
  }

  private mapGpOwnFundDdqQuota(raw: unknown): GpOwnFundDdqQuota | undefined {
    if (!raw || typeof raw !== 'object') {
      return undefined;
    }
    const o = raw as Record<string, unknown>;
    const limit = o.limit;
    if (typeof limit !== 'number') {
      return undefined;
    }
    const used =
      typeof o.usedThisMonth === 'number'
        ? o.usedThisMonth
        : typeof o.used_this_month === 'number'
        ? o.used_this_month
        : 0;
    const remaining = typeof o.remaining === 'number' ? o.remaining : 0;
    const month = typeof o.month === 'number' ? o.month : new Date().getMonth() + 1;
    const year = typeof o.year === 'number' ? o.year : new Date().getFullYear();
    return { limit, usedThisMonth: used, remaining, month, year };
  }

  private mapUsageStatistics(
    usage: any,
    month: number = new Date().getMonth() + 1,
    year: number = new Date().getFullYear(),
  ): UsageStatistics | null {
    if (!usage) {
      return null;
    }

    return {
      ddqRunsThisMonth: usage.ddqRunsThisMonth ?? usage.ddq_runs_this_month ?? 0,
      remainingDdqRuns: usage.remainingDdqRuns ?? usage.remaining_ddq_runs ?? null,
      imGenerationsTotal: usage.imGenerationsTotal ?? usage.im_generations_total ?? 0,
      canGenerateIm: usage.canGenerateIm ?? usage.can_generate_im,
      month: usage.month ?? month,
      year: usage.year ?? year,
    };
  }

  /**
   * Public so callers can map the `pendingChange` returned on a
   * `plan_change_already_scheduled` error without duplicating this mapping.
   */
  public mapPendingPlanChange(raw: any): PendingPlanChange | null {
    if (!raw) {
      return null;
    }
    const pendingTier = raw.pendingTier || raw.pending_tier;
    if (!pendingTier) {
      return null;
    }
    return {
      pendingTier: {
        uid: pendingTier.uid,
        code: pendingTier.code,
        name: pendingTier.name,
      },
      scheduledChangeAt: raw.scheduledChangeAt || raw.scheduled_change_at || null,
      scheduledChangeType: raw.scheduledChangeType || raw.scheduled_change_type || null,
    };
  }

  private mapPlanChangePreview(response: any): PlanChangePreview {
    return {
      currentTier: {
        uid: response.currentTier?.uid || response.current_tier?.uid,
        code: response.currentTier?.code || response.current_tier?.code,
        name: response.currentTier?.name || response.current_tier?.name,
      },
      targetTier: {
        uid: response.targetTier?.uid || response.target_tier?.uid,
        code: response.targetTier?.code || response.target_tier?.code,
        name: response.targetTier?.name || response.target_tier?.name,
      },
      billingInterval: (response.billingInterval || response.billing_interval || 'annual') as 'annual' | 'monthly',
      changeType: (response.changeType || response.change_type || 'upgrade') as 'upgrade' | 'downgrade',
      outstandingCredit: Number(response.outstandingCredit ?? response.outstanding_credit ?? 0),
      upgradePayableNow: Number(response.upgradePayableNow ?? response.upgrade_payable_now ?? 0),
      currentPeriodPrice: Number(response.currentPeriodPrice ?? response.current_period_price ?? 0),
      targetPeriodPrice: Number(response.targetPeriodPrice ?? response.target_period_price ?? 0),
      daysRemaining: Number(response.daysRemaining ?? response.days_remaining ?? 0),
      daysTotal: Number(response.daysTotal ?? response.days_total ?? 0),
      remainingFraction: Number(response.remainingFraction ?? response.remaining_fraction ?? 0),
      isTrial: Boolean(response.isTrial ?? response.is_trial ?? false),
      trialEnd: response.trialEnd ?? response.trial_end ?? null,
    };
  }

  /**
   * Re-read `accounts/profile`. It carries the usage the app gates on (remaining
   * DDQ runs, IM rights), so call it after any plan change or the new plan only
   * shows after a page refresh (KAP-1897). Never errors.
   */
  refreshProfile(): Observable<void> {
    return this.apiService.get<UserProfileDTO>('accounts/profile').pipe(
      tap((profileResp) => {
        this.syncFromProfileResponse(profileResp);
      }),
      map(() => void 0),
      catchError(() => of(void 0)),
    );
  }

  /**
   * Persist `accounts/profile` user fields for every authenticated role.
   * Always runs (staff, partner admin, GP/LP, client, advisor, etc.) so profile UI can render.
   */
  applyAccountProfileToStore(profileResp: unknown): void {
    if (!profileResp || typeof profileResp !== 'object') {
      return;
    }
    const userProfile = deserialize({ ...(profileResp as object) }, UserProfile);
    this.appStoreService.updateUserProfile(userProfile);
  }

  /**
   * Apply profile response to app + subscription stores.
   * Safe to call on login, profile refresh, and after org switch when profile is reloaded.
   */
  syncFromProfileResponse(profileResp: unknown): void {
    this.applyAccountProfileToStore(profileResp);
    this.syncSubscriptionFromProfileResponse(profileResp);
  }

  /** Subscription block from `accounts/profile` (skipped for staff / partner-admin). */
  private syncSubscriptionFromProfileResponse(profileResp: unknown): void {
    if (this.isStaffUser()) {
      return;
    }

    const rawSub = extractProfileSubscriptionPayload(profileResp);
    if (!rawSub) {
      return;
    }

    const now = new Date();
    const rawUsage = (rawSub as Record<string, unknown>)['usage'];
    const hasLpStyleUsage =
      !!rawUsage &&
      typeof rawUsage === 'object' &&
      ((rawUsage as Record<string, unknown>)['remaining_ddq_runs'] != null ||
        (rawUsage as Record<string, unknown>)['remainingDdqRuns'] != null ||
        typeof (rawUsage as Record<string, unknown>)['ddq_runs_this_month'] === 'number' ||
        typeof (rawUsage as Record<string, unknown>)['ddqRunsThisMonth'] === 'number');

    if (hasLpStyleUsage) {
      const usage = this.mapUsageStatistics(rawUsage, now.getMonth() + 1, now.getFullYear());
      if (usage) {
        const usageMonth = usage.month ?? now.getMonth() + 1;
        const usageYear = usage.year ?? now.getFullYear();
        this.store.setUsageStatistics(usage, usageMonth, usageYear);
      }
    }

    const current = this.store.getValue().currentSubscription;
    const merged = mergeProfileIntoSubscription(current, rawSub);
    if (merged) {
      this.store.setCurrentSubscription(merged);
    }
  }

  /**
   * Get list of available subscription tiers
   * @param tierType Optional filter by tier type ('gp' or 'lp')
   * @returns Observable<SubscriptionTier[]> - Array of subscription tiers
   */
  getAvailableTiers(tierType?: 'gp' | 'lp', forceRefresh: boolean = false): Observable<SubscriptionTier[]> {
    if (this.isStaffUser()) {
      return of([]);
    }

    if (!forceRefresh) {
      const cachedTiers = this.store.getValue().availableTiers;
      if (tierType && cachedTiers[tierType]?.length > 0) {
        return of(cachedTiers[tierType]);
      }
      if (!tierType && cachedTiers.all?.length > 0) {
        return of(cachedTiers.all);
      }
    }

    const endpoint = `subscriptions/tiers/`;
    let params = new HttpParams();
    if (tierType) {
      params = params.set('tier_type', tierType);
    }

    this.store.setTiersLoading(true);

    return this.apiService.get<any>(endpoint, { params }).pipe(
      map((response) => {
        const results = response.results || response || [];
        return results.map(
          (tier: any) =>
            ({
              uid: tier.uid,
              code: tier.code,
              name: tier.name,
              description: tier.description,
              tierType: tier.tierType || tier.tier_type || tierType || 'lp',
              isActive:
                tier.isActive !== undefined ? tier.isActive : tier.is_active !== undefined ? tier.is_active : true,
              trialPeriodDays: tier.trialPeriodDays || tier.trial_period_days,
              isTrialAvailable: tier.isTrialAvailable !== undefined ? tier.isTrialAvailable : tier.is_trial_available,
              annualPriceEur:
                tier.annualPriceEur !== undefined && tier.annualPriceEur !== null
                  ? Number(tier.annualPriceEur)
                  : tier.annual_price_eur !== undefined && tier.annual_price_eur !== null
                    ? Number(tier.annual_price_eur)
                    : null,
              monthlyPriceEur:
                tier.monthlyPriceEur !== undefined && tier.monthlyPriceEur !== null
                  ? Number(tier.monthlyPriceEur)
                  : tier.monthly_price_eur !== undefined && tier.monthly_price_eur !== null
                    ? Number(tier.monthly_price_eur)
                    : null,
              lpDdqUploadsPerMonth:
                tier.lpDdqUploadsPerMonth !== undefined && tier.lpDdqUploadsPerMonth !== null
                  ? Number(tier.lpDdqUploadsPerMonth)
                  : tier.lp_ddq_uploads_per_month !== undefined && tier.lp_ddq_uploads_per_month !== null
                    ? Number(tier.lp_ddq_uploads_per_month)
                    : null,
              availableAddons: tier.availableAddons || tier.available_addons || [],
              createdAt: tier.createdAt || tier.created_at,
              modifiedAt: tier.modifiedAt || tier.modified_at,
            } as SubscriptionTier),
        );
      }),
      tap((tiers) => {
        this.store.setAvailableTiers(tiers, tierType);
      }),
      catchError((error) => {
        const errorMessage = error?.error?.error || error?.error?.message || error?.message || 'Unknown error';
        this.store.setTiersError(errorMessage);
        throw error;
      }),
    );
  }

  /**
   * Get current subscription with trial status
   * @returns Observable<Subscription | null> - Returns null if no subscription found (404)
   */
  getCurrentSubscription(forceRefresh: boolean = false): Observable<Subscription | null> {
    if (this.isStaffUser()) {
      return of(null);
    }

    // Subscription endpoints are authenticated. During logout/session expiration,
    // components may still evaluate and call this method; prevent new HTTP calls (avoid 401).
    if (!this.isAuthenticated()) {
      this.clearCurrentSubscriptionResolution();
      this.store.setCurrentSubscription(null);
      return of(null);
    }

    if (forceRefresh) {
      this.hasResolvedCurrentSubscription = false;
      this.currentSubscriptionInFlight = null;
      this.inFlightOrganizationId = null;
    }

    const organizationId = this.getActiveOrganizationId();
    if (
      !forceRefresh &&
      this.hasResolvedCurrentSubscription &&
      this.resolvedOrganizationId === organizationId
    ) {
      return of(this.store.getValue().currentSubscription);
    }

    if (
      !forceRefresh &&
      this.currentSubscriptionInFlight &&
      this.inFlightOrganizationId === organizationId
    ) {
      return this.currentSubscriptionInFlight;
    }

    this.store.setCurrentSubscriptionLoading(true);

    // Use the enhanced endpoint /api/common/subscription/current/ which includes
    // activeAddons for GPs and trial period info for LPs
    const request$: Observable<Subscription | null> = this.apiService.get<any>(`${this.baseUrl}/current/`).pipe(
      map((response) => {
        const subscription: Subscription = {
          uid: response.uid,
          emittentCompany: response.emittent_company,
          investorCompany: response.investor_company,
          tier: response.tier
            ? {
                uid: response.tier.uid,
                code: response.tier.code,
                name: response.tier.name,
                description: response.tier.description || (response.tier as any).description || undefined,
                tierType:
                  response.tier.tierType ||
                  response.tier.tier_type ||
                  (response.emittent_company || response.emittentCompany ? 'gp' : 'lp'),
              }
            : {
                uid: '',
                code: 'free',
                name: 'Free',
                description: undefined,
                tierType: response.emittent_company || response.emittentCompany ? 'gp' : 'lp',
              },
          addons: response.addons
            ? response.addons.map((addon: any) => ({
                uid: addon.uid,
                code: addon.code,
                name: addon.name,
                description: addon.description,
                addonType: addon.addonType || addon.addon_type, // Handle both camelCase and snake_case
                isActive:
                  addon.isActive !== undefined
                    ? addon.isActive
                    : addon.is_active !== undefined
                    ? addon.is_active
                    : true, // Handle both camelCase and snake_case
                createdAt: addon.createdAt || addon.created_at,
                modifiedAt: addon.modifiedAt || addon.modified_at,
              }))
            : [],
          // Map activeAddons from the enhanced endpoint response
          activeAddons: response.activeAddons
            ? response.activeAddons.map((addon: any) => ({
                uid: addon.uid,
                code: addon.code,
                name: addon.name,
                description: addon.description,
                addonType: addon.addonType || addon.addon_type,
                isActive: true, // activeAddons are already filtered to be active
                createdAt: addon.createdAt || addon.created_at,
                modifiedAt: addon.modifiedAt || addon.modified_at,
              }))
            : [],
          usage: this.mapUsageStatistics(response.usage || response.subscription?.usage),
          gpOwnFundDdqQuota:
            this.mapGpOwnFundDdqQuota(
              response.gpOwnFundDdqQuota ?? response.gp_own_fund_ddq_quota,
            ) ?? null,
          startDate: response.start_date,
          trialEndDate: response.trial_end_date || response.trialEndDate || undefined,
          endDate: response.end_date,
          isActive:
            response.is_active !== undefined
              ? response.is_active
              : response.isActive !== undefined
              ? response.isActive
              : true,
          isTrial: response.is_trial !== undefined ? response.is_trial : response.isTrial || false,
          status: response.status || 'inactive',
          // Map trial period information from the enhanced endpoint
          isTrialPeriod:
            response.is_trial_period !== undefined
              ? response.is_trial_period
              : response.isTrialPeriod !== undefined
              ? response.isTrialPeriod
              : response.is_trial || response.isTrial || false,
          daysRemainingInTrial:
            response.days_remaining_in_trial !== undefined
              ? response.days_remaining_in_trial
              : response.daysRemainingInTrial,
          trialPeriodDays:
            response.trial_period_days !== undefined ? response.trial_period_days : response.trialPeriodDays,
          billingInterval: response.billing_interval || response.billingInterval,
          paymentStatus: response.payment_status || response.paymentStatus,
          accessState: response.access_state || response.accessState,
          pendingActivation:
            response.pending_activation !== undefined
              ? response.pending_activation
              : response.pendingActivation !== undefined
              ? response.pendingActivation
              : false,
          paymentFailureMessage:
            response.payment_failure_message || response.paymentFailureMessage || '',
          hasStripeBilling:
            response.has_stripe_billing !== undefined
              ? response.has_stripe_billing
              : response.hasStripeBilling !== undefined
              ? response.hasStripeBilling
              : undefined,
          stripeSubscriptionId: response.stripe_subscription_id || response.stripeSubscriptionId,
          pendingPlanChange: this.mapPendingPlanChange(response.pendingChange || response.pending_change),
          createdAt: response.created_at || response.createdAt,
          modifiedAt: response.modified_at || response.modifiedAt,
        };

        return subscription;
      }),
      tap((subscription) => {

        if (this.currentSubscriptionInFlight !== request$) {
          return;
        }
        this.markCurrentSubscriptionResolved(organizationId);
        this.store.setCurrentSubscription(subscription);
        this.store.setPendingPlanChange(subscription?.pendingPlanChange || null);
        if (subscription?.usage) {
          const now = new Date();
          const usageMonth = subscription.usage.month ?? now.getMonth() + 1;
          const usageYear = subscription.usage.year ?? now.getFullYear();
          this.store.setUsageStatistics(subscription.usage, usageMonth, usageYear);
        }
        // Ensure tiers are loaded for the subscription's tier type
        if (subscription?.tier?.tierType) {
          const tierType = subscription.tier.tierType;
          const availableTiers = this.store.getValue().availableTiers;
          const hasTiers = tierType === 'gp' ? availableTiers.gp?.length > 0 : availableTiers.lp?.length > 0;

          if (!hasTiers) {
            // Load tiers in background
            this.getAvailableTiers(tierType)
              .pipe(catchError(() => of([])))
              .subscribe();
          }
        }
      }),
      catchError((error) => {
        const isSuperseded = this.currentSubscriptionInFlight !== request$;

        if (isNoSubscriptionFoundError(error)) {
          if (!isSuperseded) {
            this.markCurrentSubscriptionResolved(organizationId);
            this.store.setCurrentSubscription(null);
          }
          return of(null);
        }

        if (!isSuperseded) {
          const errorMessage = error?.error?.error || error?.error?.message || error?.message || 'Unknown error';
          this.store.setCurrentSubscriptionError(errorMessage);
        }
        return throwError(() => error);
      }),
      finalize(() => {
        
        if (this.currentSubscriptionInFlight === request$) {
          this.currentSubscriptionInFlight = null;
          this.inFlightOrganizationId = null;
          this.store.setCurrentSubscriptionLoading(false);
        }
      }),
      
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    this.currentSubscriptionInFlight = request$;
    this.inFlightOrganizationId = organizationId;

    return request$;
  }

  /**
   * KAP-1849 (AC #8: "reflected in the tier badge"). A SEPA subscribe that
   * comes back pending/processing (see isPaidActivationConfirmed in the
   * upgrade dialog) is actually confirmed later by an async Stripe webhook,
   * with nothing telling the frontend to re-fetch - the tier badge (and
   * everything else reactively bound to this service's store) would
   * otherwise only pick up the change on the next unrelated page load.
   *
   * Deliberately lives here, not inside the upgrade dialog component: GP's
   * SEPA flow closes the dialog and navigates away immediately on submit
   * (unlike LP's, which stays open), so a poller tied to the dialog's own
   * lifecycle would be destroyed before it could ever see the confirmation.
   * This service is a singleton (providedIn: 'root'), so the poll survives
   * navigation.
   *
   * Not a substitute for the payment-confirmation email: a real SEPA debit
   * can take days to clear, and polling for days isn't reasonable. This only
   * covers the fast case (e.g. test mode, or a quick real confirmation) -
   * maxAttempts * intervalMs bounds it to a few minutes by default, after
   * which the email remains the actual source of truth.
   *
   * getCurrentSubscription(true) already pushes into the shared store on
   * every tick, so nothing else needs to subscribe to this method's return
   * value for the badge to update - the store update alone does it.
   */
  pollUntilTierActivated(
    targetTierCode: string,
    options: { intervalMs?: number; maxAttempts?: number } = {},
  ): void {
    this.stopActivationPolling();
    const intervalMs = options.intervalMs ?? 5000;
    const maxAttempts = options.maxAttempts ?? 36; // ~3 minutes at the default interval

    let attempts = 0;
    this.activationPollSub = timer(intervalMs, intervalMs)
      .pipe(
        exhaustMap(() => this.getCurrentSubscription(true).pipe(catchError(() => of(null)))),
      )
      .subscribe((subscription) => {
        attempts += 1;
        const activated = subscription?.tier?.code === targetTierCode;
        if (activated || attempts >= maxAttempts) {
          this.stopActivationPolling();
        }
      });
  }

  stopActivationPolling(): void {
    this.activationPollSub?.unsubscribe();
    this.activationPollSub = null;
  }

  private getActiveOrganizationId(): string | number | null {
    const activeId = this.organizationQuery.getActiveId();
    return activeId ?? null;
  }

  private markCurrentSubscriptionResolved(organizationId: string | number | null): void {
    this.hasResolvedCurrentSubscription = true;
    this.resolvedOrganizationId = organizationId;
  }

  private clearCurrentSubscriptionResolution(): void {
    this.hasResolvedCurrentSubscription = false;
    this.resolvedOrganizationId = null;
    this.currentSubscriptionInFlight = null;
    this.inFlightOrganizationId = null;
  }

  /**
   * Get trial period information
   * @param forceRefresh Force refresh from API, otherwise use cached value from store
   */
  getTrialStatus(forceRefresh: boolean = false): Observable<TrialStatus | null> {
    if (this.isStaffUser()) {
      return of(null);
    }

    // Trial-status endpoint is authenticated. During logout, components may still
    // evaluate and call this method; prevent triggering new HTTP calls.
    if (!this.isAuthenticated()) {
      this.trialStatusInFlight = null; // avoid reusing a request started before logout
      return of(null);
    }

    // Check store first if not forcing refresh
    if (!forceRefresh) {
      const cached = this.store.getValue().trialStatus;
      if (cached) {
        return of(cached);
      }
    }

    // Coalesce concurrent requests (including those triggered with forceRefresh).
    if (this.trialStatusInFlight) {
      return this.trialStatusInFlight;
    }

    this.store.setTrialStatusLoading(true);

    const request$ = this.apiService.get<TrialStatus>(`${this.baseUrl}/trial-status/`).pipe(
      tap((trialStatus) => {
        this.store.setTrialStatus(trialStatus);
      }),
      catchError((error) => {
        if (isNoSubscriptionFoundError(error)) {
          this.store.setTrialStatus(null);
          return of(null);
        }
        this.store.setTrialStatusError(
          error?.error?.error || error?.error?.message || error?.message || 'Unknown error',
        );
        throw error;
      }),
      finalize(() => {
        this.trialStatusInFlight = null;
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    this.trialStatusInFlight = request$;
    return request$;
  }

  /**
   * Upgrade to a subscription tier
   * @param tierUid UUID of the tier to upgrade to
   */
  upgradeToTier(tierUid: string): Observable<Subscription> {
    if (this.isStaffUser()) {
      throw new Error('Subscription upgrades are not available for staff users');
    }
    const payload = { tierUid };
    this.store.setCurrentSubscriptionLoading(true);
    return this.apiService.post<Subscription>(`${this.baseUrl}/upgrade/`, payload).pipe(
      switchMap(() => {
        // After upgrade, refresh subscription from server to get latest data including activeAddons and trial info
        return this.getCurrentSubscription(true);
      }),
      switchMap((subscription) =>
        this.refreshProfile().pipe(
          map(() => subscription),
        ),
      ),
      catchError((error) => {
        const errorMessage =
          error?.error?.error || error?.error?.detail || error?.error?.message || error?.message || 'Unknown error';
        this.store.setCurrentSubscriptionError(errorMessage);
        throw error;
      }),
    );
  }

  cancelTrial(): Observable<Subscription> {
    if (this.isStaffUser()) {
      throw new Error('Trial cancellation is not available for staff users');
    }
    return this.apiService.post<Subscription>(`${this.baseUrl}/cancel-trial/`, {}).pipe(
      tap((subscription) => {
        this.store.setCurrentSubscription(subscription);
      }),
    );
  }

  /**
   * Purchase a GP add-on
   * @param addonUid UUID of the add-on to purchase
   */
  purchaseAddon(addonUid: string): Observable<Subscription> {
    if (this.isStaffUser()) {
      throw new Error('Addon purchases are not available for staff users');
    }
    return this.apiService.post<Subscription>(`${this.baseUrl}/addons/purchase/`, { addonUid }).pipe(
      switchMap(() => {
        // After purchase, refresh subscription from server to get latest data including activeAddons
        return this.getCurrentSubscription(true);
      }),
    );
  }

  /**
   * Remove a GP add-on
   * @param addonUid UUID of the add-on to remove
   */
  removeAddon(addonUid: string): Observable<Subscription> {
    if (this.isStaffUser()) {
      throw new Error('Addon removal is not available for staff users');
    }
    return this.apiService.delete<Subscription>(`${this.baseUrl}/addons/${addonUid}/`).pipe(
      switchMap(() => {
        // After removal, refresh subscription from server to get latest data including activeAddons
        return this.getCurrentSubscription(true);
      }),
    );
  }

  getAvailableAddons(forceRefresh: boolean = false): Observable<SubscriptionAddon[]> {
    if (this.isStaffUser()) {
      return of([]);
    }

    if (!forceRefresh) {
      const cached = this.store.getValue().availableAddons;
      if (cached.length > 0) {
        return of(cached);
      }
    }

    this.store.setAddonsLoading(true);

    return this.apiService.get<{ count: number; results: SubscriptionAddon[] }>(`${this.baseUrl}/addons/`).pipe(
      map((response) => response.results || []),
      tap((addons) => {
        this.store.setAvailableAddons(addons);
      }),
      catchError((error) => {
        const errorMessage = error?.error?.error || error?.error?.message || error?.message || 'Unknown error';
        this.store.setAddonsError(errorMessage);
        throw error;
      }),
    );
  }

  /**
   * Get usage statistics for the current organization
   * @param month Optional month (1-12), defaults to current month
   * @param year Optional year, defaults to current year
   * @param forceRefresh Force refresh profile/subscription from API
   * @returns Observable<UsageStatistics | null> - Derived from current subscription payload
   */
  getUsageStatistics(month?: number, year?: number, forceRefresh: boolean = false): Observable<UsageStatistics | null> {
    if (this.isStaffUser()) {
      return of(null);
    }
    if (!this.isAuthenticated()) {
      return of(null);
    }

    const currentDate = new Date();
    const targetMonth = month || currentDate.getMonth() + 1;
    const targetYear = year || currentDate.getFullYear();
    const key = `${targetYear}-${String(targetMonth).padStart(2, '0')}`;

    const cached = this.store.getValue().usageStatistics[key];
    if (cached && !forceRefresh) {
      return of(cached);
    }

    this.store.setUsageLoading(true);

    return this.getCurrentSubscription(forceRefresh).pipe(
      map((subscription) => {
        const usage = subscription?.usage;
        if (!usage) {
          return null;
        }
        const usageMonth = usage.month ?? targetMonth;
        const usageYear = usage.year ?? targetYear;
        if (usageMonth === targetMonth && usageYear === targetYear) {
          return usage;
        }
        return null;
      }),
      tap((usage) => {
        if (usage) {
          const usageMonth = usage.month ?? targetMonth;
          const usageYear = usage.year ?? targetYear;
          this.store.setUsageStatistics(usage, usageMonth, usageYear);
          return;
        }
        this.store.setUsageStatistics(null, targetMonth, targetYear);
      }),
      catchError((error) => {
        const errorMessage = error?.error?.error || error?.error?.message || error?.message || 'Unknown error';
        this.store.setUsageError(errorMessage);
        return of(null);
      }),
    );
  }

  /**
   * Check if organization has access to a specific feature
   * @param feature Feature name to check (e.g., 'ddq_run', 'im_generation', 'featured_fund')
   * @returns Observable with feature access information, including remainingRuns for DDQ
   */
  checkFeatureAccess(feature: string): Observable<FeatureAccess | null> {
    if (this.isStaffUser()) {
      return of(null);
    }
    if (!feature) {
      throw new Error('Feature parameter is required');
    }
    const params = new HttpParams().set('feature', feature);
    return this.apiService.get<FeatureAccess>(`subscriptions/feature-check/`, { params }).pipe(
      catchError((error) => {
        if (error?.status === 404 || error?.status === 400) {
          return of(null);
        }
        throw error;
      }),
    );
  }

  /**
   * Preflight before starting an issuer own-fund DDQ run (AI engine).
   * Response keys are snake_case from DRF.
   */
  getGpOwnFundDdqCanStart(): Observable<GpOwnFundDdqCanStartResponse> {
    if (!this.isAuthenticated()) {
      throw new Error('GP own-fund DDQ preflight requires an authenticated user');
    }
    if (this.isStaffUser() || this.isSuperUser()) {
      return of(this.bypassGpOwnFundDdqCanStartForPrivilegedUser());
    }
    return this.apiService.get<GpOwnFundDdqCanStartResponse>('subscriptions/gp/ddq-own-fund/can-start/');
  }

  previewPlanChange(
    tierCode: string,
    billingInterval: 'annual' | 'monthly' = 'annual',
  ): Observable<PlanChangePreview> {
    if (this.isStaffUser()) {
      throw new Error('Plan change preview is not available for staff users');
    }
    this.store.setPlanChangeLoading(true);
    return this.apiService
      .post<any>(`${this.baseUrl}/change/preview/`, {
        tierCode,
        billingInterval,
      })
      .pipe(
        map((response) => this.mapPlanChangePreview(response)),
        tap((preview) => this.store.setPlanChangePreview(preview)),
        catchError((error) => {
          const errorMessage = error?.error?.error || error?.error?.message || error?.message || 'Unknown error';
          this.store.setPlanChangeError(errorMessage);
          throw error;
        }),
      );
  }

  /**
   * KAP-1891. What an issuer (GP) upgrade to `tierCode` would charge right now -
   * the "Amount due now" the upgrade dialog shows before the issuer confirms.
   * Same shape as the investor preview. GP tiers are monthly-only; the amount
   * comes from the backend (Stripe's own invoice preview), so it matches what is
   * invoiced. Read-only: it does not touch the plan-change store used by the
   * investor flow.
   */
  previewGpUpgrade(tierCode: string): Observable<PlanChangePreview> {
    if (this.isStaffUser()) {
      throw new Error('Plan change preview is not available for staff users');
    }
    return this.apiService
      .post<any>('subscriptions/billing/gp/upgrade/preview/', { tier_code: tierCode })
      .pipe(
        map((response) => ({
          ...this.mapPlanChangePreview(response),
          startsFresh: Boolean(response?.startsFresh ?? response?.starts_fresh ?? false),
        })),
      );
  }

  confirmPlanChange(
    tierCode: string,
    billingInterval: 'annual' | 'monthly' = 'annual',
  ): Observable<Subscription | null> {
    if (this.isStaffUser()) {
      throw new Error('Plan change confirmation is not available for staff users');
    }
    this.store.setPlanChangeLoading(true);
    return this.apiService
      .post<any>(`${this.baseUrl}/change/confirm/`, {
        tierCode,
        billingInterval,
      })
      .pipe(
        switchMap(() => this.getCurrentSubscription(true)),
        tap(() => this.store.setPlanChangePreview(null)),
        catchError((error) => {
          const errorMessage = error?.error?.error || error?.error?.message || error?.message || 'Unknown error';
          this.store.setPlanChangeError(errorMessage);
          throw error;
        }),
      );
  }

  schedulePlanChange(
    tierCode: string,
    billingInterval: 'annual' | 'monthly' = 'annual',
  ): Observable<Subscription | null> {
    if (this.isStaffUser()) {
      throw new Error('Plan change scheduling is not available for staff users');
    }
    this.store.setPlanChangeLoading(true);
    return this.apiService
      .post<any>(`${this.baseUrl}/change/schedule/`, {
        tierCode,
        billingInterval,
      })
      .pipe(
        switchMap(() => this.getCurrentSubscription(true)),
        switchMap((subscription) =>
          this.getPendingPlanChange(true).pipe(
            map(() => subscription),
          ),
        ),
        tap(() => this.store.setPlanChangePreview(null)),
        catchError((error) => {
          const errorMessage = error?.error?.error || error?.error?.message || error?.message || 'Unknown error';
          this.store.setPlanChangeError(errorMessage);
          throw error;
        }),
      );
  }

  cancelScheduledPlanChange(): Observable<Subscription | null> {
    if (this.isStaffUser()) {
      throw new Error('Plan change cancellation is not available for staff users');
    }
    this.store.setPlanChangeLoading(true);
    // KAP-1892: an issuer's scheduled downgrade lives in Stripe (cancellation or a
    // price swap at period end), so it is undone through the GP billing endpoint;
    // an investor's is a note on the subscription, cleared by the schedule endpoint.
    const isGp = this.store.getValue().currentSubscription?.tier?.tierType === 'gp';
    const cancel$ = isGp
      ? this.apiService.delete<any>('subscriptions/billing/gp/downgrade/')
      : this.apiService.delete<any>(`${this.baseUrl}/change/schedule/`);
    return cancel$.pipe(
      switchMap(() => this.getCurrentSubscription(true)),
      tap(() => {
        this.store.setPendingPlanChange(null);
        this.store.setPlanChangePreview(null);
      }),
      catchError((error) => {
        const errorMessage = error?.error?.error || error?.error?.message || error?.message || 'Unknown error';
        this.store.setPlanChangeError(errorMessage);
        throw error;
      }),
    );
  }

  getPendingPlanChange(forceRefresh: boolean = false): Observable<PendingPlanChange | null> {
    if (this.isStaffUser()) {
      return of(null);
    }

    if (!forceRefresh) {
      const cached = this.store.getValue().pendingPlanChange;
      if (cached) {
        return of(cached);
      }
    }

    this.store.setPlanChangeLoading(true);
    return this.apiService.get<any>(`${this.baseUrl}/change/pending/`).pipe(
      map((response) => this.mapPendingPlanChange(response.pendingChange || response.pending_change)),
      tap((pending) => {
        this.store.setPendingPlanChange(pending);
        this.store.setPlanChangeLoading(false);
      }),
      catchError((error) => {
        if (error?.status === 404) {
          this.store.setPendingPlanChange(null);
          this.store.setPlanChangeLoading(false);
          return of(null);
        }
        const errorMessage = error?.error?.error || error?.error?.message || error?.message || 'Unknown error';
        this.store.setPlanChangeError(errorMessage);
        throw error;
      }),
    );
  }
}
